// Customer Card Component
// Creates customer card UI elements for staff view

import { getSellerNameByEmail, canSellerPerformActions } from '../services/seller-service.js';
import { formatFullName, formatAttentionType } from '../utils/formatters.js';
import { formatRegistrationTime, calculateActualWaitTime, formatWaitTime } from '../utils/time-calculator.js';
import { STATUS } from '../config/constants.js';
import stateManager from '../core/state-manager.js';

/**
 * Create a customer card element
 *
 * @param {object} customer - Customer data
 * @param {boolean} isCurrentUserBusy - Whether current user is busy with another customer
 * @returns {Promise<HTMLElement>} Customer card DOM element
 */
export async function createCustomerCard(customer, isCurrentUserBusy) {
    const card = document.createElement('div');

    const reactivatedClass = customer.wasReactivated ? 'reactivated-customer' : '';
    card.className = `p-3 bg-white rounded-lg shadow ring-1 ring-gray-200 status-${customer.status} flex items-center justify-between gap-4 ${reactivatedClass}`;

    // Build card content
    const fullName = formatFullName(customer.firstName, customer.lastName);
    const isPreferentialIcon = customer.isPreferential ? getPreferentialIcon() : '';
    const registrationTime = formatRegistrationTime(customer.createdAt);
    const waitTime = calculateActualWaitTime(customer.createdAt);
    const waitTimeDisplay = formatWaitTime(waitTime);

    // Get seller states
    const currentUser = stateManager.getState('currentUser');
    const isShiftActive = stateManager.getState('isShiftActive');
    const isSellerPaused = stateManager.getState('isSellerPaused');
    const isSellerActive = canSellerPerformActions(isShiftActive, isSellerPaused);

    // Generate buttons based on status
    const buttonsHtml = await generateActionButtons(
        customer,
        currentUser,
        isCurrentUserBusy,
        isSellerActive
    );

    // Get seller names
    const sellersData = stateManager.getState('sellersData');
    const attendedByName = customer.attendedBy
        ? await getSellerNameByEmail(customer.attendedBy, sellersData)
        : '';
    const calledByName = customer.calledByName || '...';

    card.innerHTML = `
        <div class="flex-grow">
            <div class="flex items-center gap-2">
                 ${isPreferentialIcon}
                 <p class="font-bold text-lg">${fullName}</p>
                 ${customer.isPreferential && customer.status === STATUS.WAITING ? getRemovePreferentialButton(customer.id) : ''}
            </div>
            <p class="text-sm mt-1"><b>Registrado:</b> ${registrationTime} | <b>Motivo:</b> ${formatAttentionType(customer.attentionType)} | <b>En espera:</b> ${waitTimeDisplay} min</p>
            <div class="text-xs font-semibold mt-1 h-4">
                ${customer.status === STATUS.SERVING && attendedByName ? `<span style="color:var(--color-primary-darker)">Atendido por: ${attendedByName}</span>` : ''}
                ${customer.status === STATUS.CALLING ? `<span class="text-blue-600">Llamando por: ${calledByName}</span>` : ''}
            </div>
        </div>
        <div class="flex-shrink-0 flex items-center space-x-2">${buttonsHtml}</div>`;

    return card;
}

/**
 * Get preferential icon SVG
 * @returns {string} SVG icon HTML
 */
function getPreferentialIcon() {
    return `<svg class="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" title="Atención Preferencial"><path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"></path></svg>`;
}

/**
 * Get remove preferential button HTML
 * @param {string} customerId - Customer ID
 * @returns {string} Button HTML
 */
function getRemovePreferentialButton(customerId) {
    return `<button data-id="${customerId}" class="remove-pref-btn text-xs bg-red-100 text-red-700 px-2 py-1 rounded-md hover:bg-red-200">Quitar pref.</button>`;
}

/**
 * Generate action buttons based on customer status
 *
 * @param {object} customer - Customer data
 * @param {object} currentUser - Current authenticated user
 * @param {boolean} isCurrentUserBusy - Whether user is busy
 * @param {boolean} isSellerActive - Whether seller can perform actions
 * @returns {Promise<string>} Buttons HTML
 */
async function generateActionButtons(customer, currentUser, isCurrentUserBusy, isSellerActive) {
    const btnBaseClasses = "text-white font-bold py-2 px-3 rounded-md text-sm w-28 text-center";

    switch (customer.status) {
        case STATUS.WAITING:
            return generateWaitingButtons(customer.id, isCurrentUserBusy, isSellerActive, btnBaseClasses);

        case STATUS.CALLING:
            return generateCallingButtons(customer, currentUser, isSellerActive, btnBaseClasses);

        case STATUS.SERVING:
            return generateServingButtons(customer, currentUser, isSellerActive, btnBaseClasses);

        case STATUS.STANDBY:
            return generateStandbyButtons(customer.id, isSellerActive, btnBaseClasses);

        default:
            return '';
    }
}

/**
 * Generate buttons for waiting status
 */
function generateWaitingButtons(customerId, isCurrentUserBusy, isSellerActive, btnBaseClasses) {
    const disabled = (isCurrentUserBusy || !isSellerActive) ? 'disabled title="Debes iniciar jornada y no estar en pausa para llamar clientes"' : '';
    const buttonClass = (isCurrentUserBusy || !isSellerActive) ? 'bg-gray-300 cursor-not-allowed' : 'bg-[var(--color-primary)] hover:bg-[var(--color-primary-darker)]';

    return `<button data-id="${customerId}" class="call-btn ${buttonClass} ${btnBaseClasses}" ${disabled}>Llamar</button>`;
}

/**
 * Generate buttons for calling status
 */
function generateCallingButtons(customer, currentUser, isSellerActive, btnBaseClasses) {
    if (currentUser && currentUser.email === customer.calledBy) {
        const disabled = !isSellerActive ? 'disabled' : '';

        return `<button data-id="${customer.id}" class="no-show-btn ${!isSellerActive ? 'bg-gray-300 cursor-not-allowed' : 'bg-slate-400 hover:bg-slate-500'} ${btnBaseClasses}" ${disabled}>No se presentó</button>
                <button data-id="${customer.id}" class="start-attention-btn ${!isSellerActive ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'} ${btnBaseClasses}" ${disabled}>Iniciar Atención</button>`;
    } else {
        return `<span class="text-sm font-semibold text-blue-600 w-28 text-center">Llamando...</span>`;
    }
}

/**
 * Generate buttons for serving status
 */
function generateServingButtons(customer, currentUser, isSellerActive, btnBaseClasses) {
    if (currentUser && currentUser.email === customer.attendedBy) {
        const disabled = !isSellerActive ? 'disabled' : '';
        const buttonClass = !isSellerActive ? 'bg-gray-300 cursor-not-allowed' : 'bg-[var(--color-primary-darker)] hover:bg-[var(--color-primary)]';

        return `<button data-id="${customer.id}" class="finish-btn ${buttonClass} ${btnBaseClasses}" ${disabled}>Finalizar</button>`;
    } else {
        return `<span class="inline-flex items-center justify-center ${btnBaseClasses} bg-[var(--color-primary-darker)] cursor-not-allowed">Atendiendo</span>`;
    }
}

/**
 * Generate buttons for standby status
 */
function generateStandbyButtons(customerId, isSellerActive, btnBaseClasses) {
    const disabled = !isSellerActive ? 'disabled' : '';
    const buttonClass = !isSellerActive ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600';

    return `<button data-id="${customerId}" class="reactivate-btn ${buttonClass} ${btnBaseClasses}" ${disabled}>Reactivar</button>`;
}
